'use client'

import { useState, FormEvent, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { signIn } from '@/lib/auth-client'

function LoginInner() {
  const router = useRouter()
  const params = useSearchParams()
  const next = params.get('next') || '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await signIn.email({ email, password, callbackURL: next })
      if (res.error) {
        setError(res.error.message ?? 'Sign-in failed')
      } else {
        router.push(next)
        router.refresh()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-6 relative overflow-hidden">

      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      {/* Main card */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-xl w-full">

        {/* Logo + Title side by side */}
        <div className="flex items-center gap-4 mb-3">
          <Image
            src="/ChatoorAI-Logo.png"
            alt="ChatoorAI"
            width={72}
            height={72}
            className="rounded-2xl shadow-2xl flex-shrink-0"
          />
          <div className="text-left">
            <h1 className="text-4xl font-extrabold text-white tracking-tight leading-none">
              Aether AI Studio<sup className="text-lg font-normal" style={{ verticalAlign: '8px', marginLeft: '2px' }}>™</sup>
            </h1>
            <p className="text-sm text-orange-400 font-semibold tracking-wide mt-1">by ChatoorAI</p>
          </div>
        </div>

        {/* Tagline */}
        <p className="text-3xl font-bold text-white mb-3">Think it. Ship it.</p>
        <p className="text-slate-200 text-sm leading-relaxed mb-2 max-w-md">
          Submit an idea. Watch AI agents design, build, and ship it — end to end.
        </p>
        <p className="text-orange-400 text-sm font-semibold mb-8">
          One Product Idea, 24 AI Agents, Available 24x7 — Your Idea Ships
        </p>

        {/* Sign-in card */}
        <form
          onSubmit={onSubmit}
          className="w-full max-w-xs border border-slate-700 rounded-xl p-5 bg-slate-800/60 backdrop-blur-sm shadow-2xl"
        >
          <p className="text-sm font-semibold text-slate-200 mb-4 text-left">Sign in to continue</p>

          <div className="space-y-2.5">
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-900/70 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <input
              type="password"
              required
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-900/70 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />

            {error && (
              <div className="text-xs text-red-300 bg-red-900/40 border border-red-700/60 rounded-lg px-3 py-2 text-left">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-lg shadow-lg shadow-orange-500/30 transition-all hover:shadow-orange-500/50 hover:scale-[1.02] active:scale-100 disabled:opacity-50 disabled:hover:scale-100"
            >
              {busy ? 'Signing in…' : 'Sign in →'}
            </button>
          </div>

          <p className="text-[11px] text-slate-400 text-center mt-3">
            New here?{' '}
            <Link href="/register" className="text-orange-400 hover:text-orange-300 font-semibold">
              Create an account
            </Link>
          </p>
        </form>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 text-center space-y-1">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} ChatoorAI, Inc. All rights reserved.
        </p>
        <p className="text-[10px] text-slate-500">
          Aether AI Studio™ v0.9.0 · Proprietary Software · Pre-Release
        </p>
      </div>
    </div>
  )
}

export default function LoginPage() {
  // useSearchParams() requires Suspense in app router builds
  return (
    <Suspense fallback={null}>
      <LoginInner />
    </Suspense>
  )
}
